import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import React, { ReactElement, useEffect, useState } from "react";
import { useTheme } from "styled-components";
import { Box, HBox } from "@ui/box/Box";
import Spacer from "@ui/spacer/Spacer";
import { Typo } from "@ui/text/Typo";
import { ITheme } from "@ui/theme/GlobalTheme";
import { useWindowSize } from "@ui/utils/useResize";
import { AdaptiveTabBar } from "./AdaptiveTabBar";
import { ChevronDown } from "lucide-react";

// Frontend implementation of @ui/tabs/Tabs (see Vite/TypeScript aliases).
// Preserve the shared UI contract and tab appearance while using overflow menus
// throughout this app, without changing the sibling UI package.

// STYLES
export const getDefaultTabsLightTheme = {
	backgroundHover: "rgb(206, 215, 235)",
};

export const getDefaultTabsDarkTheme: typeof getDefaultTabsLightTheme = {
	backgroundHover: "rgb(206, 215, 235)",
};

export interface ITabsProps {
	children?: (ReactElement<ITabItemProps> | null | false)[] | ReactElement<ITabItemProps>;
	selected?: number;
	sx?: React.CSSProperties;
	scrollable?: boolean;
	background?: string;
	spaceLeft?: number;
	spaceBottom?: number;
	rounds?: boolean;
	decoration?: React.ReactElement;
	title?: string;
	onSectionSelect?: (section: number) => void;
	checkFeature?: (feature: string) => boolean;
	variant?: "modern" | "round" | "strong";
}

export interface ITabItemProps {
	title: string;
	children?: any;
	section?: number;
	type?: "default" | "spacer" | "button";
	onClick?: () => void;
	sx?: React.CSSProperties;
	/** Icon to render before the title in modern-variant tabs. Pass any React node
	 *  (e.g. a lucide-react icon: `<CreditCard size={16} />`). Other variants ignore it. */
	icon?: React.ReactNode;
}

export const Tabs = ({
	children,
	scrollable,
	background,
	selected,
	sx,
	spaceLeft,
	spaceBottom,
	rounds,
	decoration,
	title,
	checkFeature,
	onSectionSelect,
	variant,
}: ITabsProps) => {
	useLocale();
	if (selected === undefined || isNaN(selected)) selected = 0;

	if (children) {
		if (!Array.isArray(children) && React.isValidElement(children)) children = [children];
	}

	const hiddenChilds: any[] = [];

	if (children && checkFeature) {
		children.map(val => {
			if (!val) return null;
			React.Children.toArray(val.props.children).forEach(child => {
				if (!React.isValidElement(child)) return true;
				const component = child.type;
				const feature = (component as any).feature;
				if (feature) {
					// check feature
					if (checkFeature && !checkFeature(feature)) {
						hiddenChilds.push(val);
					}
				}
			});
		});

		//selected = undefined;

		if (hiddenChilds.length > 0 && children.length > 0) {
			for (let index = 0; index < children.length; index++) {
				if (!hiddenChilds.find(fval => fval === children[index])) {
					selected = index;
					break;
				}
			}
		}
	}

	const [currentSelection, setCurrentSelection] = useState(0);

	useEffect(() => {
		if (selected !== undefined) {
			// todo need this line?
			onSelectChange(selected);
			setCurrentSelection(selected);
		}
	}, [selected]);

	const theme = useTheme() as ITheme;
	const style = theme.tabs;
	const { width: viewportWidth } = useWindowSize();
	const isMobile = viewportWidth <= 768;
	const isRound = rounds || variant === "round";
	const tabPadding = isRound ? "6px 16px" : isMobile ? "10px 14px" : variant === "strong" ? "12px 24px" : variant === "modern" ? "11px 20px" : "15px 20px";
	const tabTextStyle = {
		whiteSpace: "nowrap",
		textOverflow: "ellipsis",
		overflow: "hidden",
		fontSize: isRound ? ".8rem" : variant === "modern" || variant === "strong" ? ".85rem" : ".9rem",
		...(variant === "modern" ? { fontWeight: 500, letterSpacing: "0.01em" } : {}),
		...(variant === "strong" ? { fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" } : {}),
	};

	if (!children) return null;

	const onSelectChange = (index: number) => {
		for (let i = 0; i < children.length; i++) {
			let child = children[i];
			if (child && typeof child === "object" && "props" in child) {
				if (child.props.type === "spacer" || child.props.type === "button") {
					return;
				}
			}
		}

		setCurrentSelection(index);

		if (children.length > index) {
			if (!children[index] || !children[index].props) return;
			if (!React.isValidElement(children[index])) return;
			const { section } = children[index].props;
			onSectionSelect && onSectionSelect(section ?? index);
		}
	};

	let tabs = null;
	if (rounds || variant === "round") {
		tabs = children?.map((val, index) => {
			if (!val) return null;
			if (hiddenChilds && hiddenChilds.find(fval => fval === val)) return null;
			const props = val.props as ITabItemProps;

			if (props.type === "spacer") {
				return <Spacer key={index} grow />;
			}

			return (
				<HBox
					key={index}
					sx={{
						padding: tabPadding,
						cursor: "pointer",
						overflow: "hidden",
						backgroundColor: currentSelection === index ? "#1f3344" : "transparent",
						border: currentSelection === index ? "1px solid rgba(0,0,0,0)" : "1px solid rgba(0,0,0,.1)",
						borderRadius: "16px",
						transition: "background-color .3s, border .3s, color .3s",
						"&:hover": {
							backgroundColor: currentSelection === index ? undefined : "tabsHoverColor",
						},
						...props.sx,
					}}
					onClick={() => {
						if (props.onClick) {
							props.onClick();
							return;
						}

						if (props.type !== "spacer" && props.type !== "button") {
							onSelectChange(index);
							setCurrentSelection(index);
						}
					}}>
					<Typo
						color={currentSelection === index ? "white" : "textPrimary"}
						sx={{
							...tabTextStyle,
						}}>
						{val.props && val.props.title}
					</Typo>
				</HBox>
			);
		});
	} else {
		tabs = children?.map((val, index) => {
			if (!val) return null;

			if (hiddenChilds && hiddenChilds.find(fval => fval === val)) return null;

			const isModern = variant === "modern";
			const isStrong = variant === "strong";
			const isSelected = currentSelection === index;
			const tabIcon = (val.props as ITabItemProps)?.icon;
			return (
				<HBox
					key={index}
					sx={{
						gap: isModern && tabIcon ? "8px" : undefined,
						alignItems: "center",
						padding: tabPadding,
						cursor: "pointer",
						position: isModern ? ("relative" as const) : undefined,
						// Modern: underline lives in `::after` so we can animate its scaleX in/out
						// smoothly. Non-modern variants keep the old border-bottom (no animation).
						borderBottom: isModern
							? "1px solid rgba(0,0,0,.05)"
							: isSelected
								? isStrong ? "3px solid textPrimary" : "2px solid textPrimary"
								: isStrong ? "3px solid rgba(0,0,0,.1)" : "2px solid rgba(0,0,0,.1)",
						borderTopLeftRadius: isModern ? "6px" : undefined,
						borderTopRightRadius: isModern ? "6px" : undefined,
						color: isSelected ? "textPrimary" : "textSecondary",
						transition: "background-color .15s, color .15s, transform .18s",
						overflow: isMobile ? "visible" : "hidden",
						flexShrink: 0,
						"&:hover": {
							backgroundColor: isSelected ? undefined : style?.backgroundHover,
							color: "textPrimary",
							// Subtle "lift" on hover for modern — feels touchable without being playful.
							transform: isModern && !isSelected ? "translateY(-1px)" : undefined,
						},
						// Animated underline: a pseudo-element below the tab that grows in / shrinks
						// out via scaleX. transform-origin keeps the animation centered on the tab,
						// so it expands from the middle. 3px thickness matches the previous static
						// underline for visual continuity.
						...(isModern
							? {
								"&::after": {
									content: '""',
									position: "absolute",
									left: 0,
									right: 0,
									bottom: "-1px",
									height: "3px",
									backgroundColor: "textPrimary",
									transform: isSelected ? "scaleX(1)" : "scaleX(0)",
									transformOrigin: "center",
									transition: "transform .22s cubic-bezier(.4, 0, .2, 1)",
									borderRadius: "2px 2px 0 0",
								},
							}
							: {}),
					}}
					onClick={() => {
						onSelectChange(index);
						setCurrentSelection(index);
					}}>
					{isModern && tabIcon && (
						<Box
							sx={{
								display: "inline-flex",
								alignItems: "center",
								color: "inherit",
								// Selected icon nudged up by ~10% — emphasis without obvious "growing".
								transform: isSelected ? "scale(1.08)" : "scale(1)",
								transition: "transform .18s ease, color .15s",
							}}>
							{tabIcon}
						</Box>
					)}
					<Typo
						block
						bold={isStrong ? true : undefined}
						sx={{
							...tabTextStyle,
							...(isModern && isSelected ? { fontWeight: 600 } : {}),
						}}>
						{val.props && val.props.title}
					</Typo>
				</HBox>
			);
		});
	}

	if (typeof children[currentSelection] === "boolean") return null;

	let current = currentSelection !== undefined && children[currentSelection]?.props?.children;
	if (!current) return null;

	if (scrollable) {
		current = (
			<Box
				sx={{
					overflow: "auto",
					flexGrow: 1,
					display: "flex",
					flexDirection: "column",
				}}>
				{current}
			</Box>
		);
	} else if (sx) {
		current = <Box>{current}</Box>;
	}

	const adaptiveItems = (tabs ?? []).flatMap((node, index) => {
		const child = children[index];
		if (!node || !child || child.props.type === "spacer") return [];
		return [{
			id: String(index),
			label: child.props.title,
			selected: currentSelection === index,
			content: React.cloneElement(node, { onClick: undefined }),
			onSelect: () => node.props.onClick?.(),
		}];
	});

	return <>
		{title && <Typo variant="small" color="textSecondary" block>{title}</Typo>}
		<AdaptiveTabBar
			items={adaptiveItems}
			gap={rounds || variant === "round" ? 5 : variant === "modern" ? 7 : 0}
			sx={{ "--tab-measure-weight": variant === "strong" ? 700 : 600, background: background ?? "transparent", marginLeft: spaceLeft ? `${spaceLeft}px` : undefined, ...sx }}
			after={decoration}
			renderMore={count => <HBox sx={{
				alignItems: "center", gap: "8px", padding: tabPadding,
				color: isRound ? "textPrimary" : "textSecondary",
				border: isRound ? "1px solid rgba(0,0,0,.1)" : undefined,
				borderBottom: isRound ? undefined : variant === "modern" ? "1px solid rgba(0,0,0,.05)" : variant === "strong" ? "3px solid rgba(0,0,0,.1)" : "2px solid rgba(0,0,0,.1)",
				borderRadius: isRound ? "16px" : undefined,
				borderTopLeftRadius: variant === "modern" ? "6px" : undefined,
				borderTopRightRadius: variant === "modern" ? "6px" : undefined,
				"&:hover": { backgroundColor: style?.backgroundHover, color: "textPrimary" },
			}}>
				<Typo block sx={tabTextStyle}>{msg("more")}{count})</Typo>
				<ChevronDown size={14} aria-hidden="true" />
			</HBox>}
		/>
		{spaceBottom && <Spacer size={`${spaceBottom}px`} />}
		{current}
	</>;
};
export const TabItem: React.FC<ITabItemProps> = ({ children }) => {
	useLocale();
	return <>{children}</>;
};
